import { defaultAttachmentOderDesOptions } from '@nxsys/constants/AppConstants';
import { TypedMessages } from '@nxsys/hooks/useTypedIntl';
import { IAttachmentOrder } from '@nxsys/types/models/attachmentOrder';
import { PayrollCountryType } from '@nxsys/types/models/Common';
import { formatFullName, showCurrencyByCountry } from '@nxsys/utils';
import { formatMoney } from '@nxsys/utils/number-helper';
import { ColumnsType } from 'antd/es/table';
import { find } from 'lodash';
import moment from 'moment';

export const getAttachmentOrdersReportColumns = ({
  messages,
  payrollCountry,
  isBureau,
}: {
  messages: TypedMessages;
  payrollCountry: PayrollCountryType;
  isBureau?: boolean;
}): ColumnsType<IAttachmentOrder> => {
  return [
    {
      key: 'employeeId',
      title: messages['common.employeeId'],
      width: 180,
      render: (record) =>
        record?.subcontractor?.id || record?.subcontractorId || '_',
    },
    {
      key: 'employeeName',
      title: messages['common.name'],
      width: 240,
      render: (record) =>
        formatFullName(record?.subcontractor) || '_',
    },
    {
      key: 'employerName',
      title: isBureau ? (messages['table.employer'] || 'Employer') : (messages['table.paymentCompany'] || 'Employer Name'),
      width: 240,
      render: (record) =>
        record?.subcontractor?.companyPayment?.companyName || '_',
    },
    {
      key: 'deductionDesc',
      title: messages['common.deductionDescription'],
      dataIndex: 'deductionDesc',
      width: 220,
      render: (value) =>
        find(defaultAttachmentOderDesOptions, (item) => item.value === value)
          ?.label || '_',
    },
    {
      key: 'effectiveDate',
      title: messages['common.effectiveDate'],
      dataIndex: 'effectiveDate',
      align: 'right',
      width: 170,
      render: (value) =>
        value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : '_',
    },
    {
      key: 'endDate',
      title: messages['common.endDate'],
      dataIndex: 'endDate',
      align: 'right',
      width: 170,
      render: (value) =>
        value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : '_',
    },
    {
      key: 'deductionMethod',
      title: messages['common.deductionMethod'],
      width: 180,
      render: (record) =>
        find(defaultAttachmentOderDesOptions, (item) => item.value === record?.deductionDesc)
          ?.type || 'Table',
    },
    {
      key: 'deductionRate',
      title: messages['common.deductionOrRate'],
      dataIndex: 'deductionRate',
      align: 'right',
      width: 180,
      render: (value) =>
        formatMoney(value, showCurrencyByCountry(payrollCountry)),
    },
    {
      key: 'totalAttachment',
      title: messages['common.totalAttachment'],
      dataIndex: 'totalAttachment',
      align: 'right',
      width: 180,
      // NX-5642: empty/0 total means the order has no cap, so show '_'.
      render: (value) =>
        value > 0
          ? formatMoney(value, showCurrencyByCountry(payrollCountry))
          : '_',
    },
    {
      key: 'totalDeduction',
      title: messages['common.totalDeduction'],
      dataIndex: 'totalDeduction',
      align: 'right',
      width: 180,
      render: (value) =>
        formatMoney(value, showCurrencyByCountry(payrollCountry)),
    },
    {
      key: 'marginFee',
      title: messages['common.marginFee'],
      align: 'center' as const,
      render: (record: IAttachmentOrder) => record.marginFee ? '£1' : '_',
    },
  ];
};
